import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useFormatter, useLocale, useTranslations } from "use-intl";
import { ChevronRight, Flame, Footprints, Info, Play, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { startOfWeek, weekdayIndex } from "@liftquest/shared/dates";
import { achievementIcon, levelFromXp, rankForLevel } from "@liftquest/shared/gamification";
import { ll, lt } from "@liftquest/shared/localized";
import { effectiveSets } from "@liftquest/shared/plan-logic";
import { Page } from "@/components/app/page-header";
import { PendingButton } from "@/components/app/pending-button";
import { PageLoading } from "@/components/app/states";
import { LevelBadge, XpBar } from "@/components/app/xp-bar";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api, call } from "@/lib/api";
import { workoutMinutes } from "@/lib/duration";
import { useDashboard, useMe } from "@/lib/queries";
import { cn } from "@/lib/utils";

/** Starts (or resumes) a workout and opens the guided screen. */
function useStartWorkout() {
  const navigate = useNavigate();
  const [pending, setPending] = useState<string | null>(null);
  const tc = useTranslations("common");
  const start = async (workoutId: string) => {
    setPending(workoutId);
    try {
      const { id } = await call(api.sessions.$post({ json: { workoutId } }));
      navigate(`/train/${id}`);
    } catch {
      // Starting needs the server (e.g. no connection in the changing room).
      toast.error(tc("error"));
    } finally {
      setPending(null);
    }
  };
  return { start, pending };
}

export function DashboardPage() {
  const me = useMe().data!;
  const { user, profile } = me;
  const locale = useLocale();
  const t = useTranslations("dashboard");
  const tc = useTranslations("common");
  const tr = useTranslations("ranks");
  const ta = useTranslations("achievements");
  const tw = useTranslations("weekdays.short");
  const format = useFormatter();
  const dashboard = useDashboard();
  const { start, pending } = useStartWorkout();
  if (!dashboard.data) return <PageLoading />;
  const d = {
    ...dashboard.data,
    thisWeek: dashboard.data.thisWeek.map((s) => ({ ...s, startedAt: new Date(s.startedAt) })),
  };

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 11 ? "morning" : hour < 18 ? "afternoon" : "evening";
  const lvl = levelFromXp(profile.xp);
  const today = weekdayIndex(now);
  const strengthThisWeek = d.thisWeek.filter((s) => s.kind === "strength");
  const trainedToday = d.thisWeek.some((s) => weekdayIndex(s.startedAt) === today);
  const isTrainingDay = d.plan?.trainingDays.includes(today);
  const isCardioDay = d.plan?.cardioDays.includes(today);
  const weekStart = startOfWeek(now);
  const hasWarmup = !!d.plan && ll(d.plan.warmup, locale).length > 0;
  const nextMinutes = d.next ? workoutMinutes(d.next.exercises, d.phase, hasWarmup) : 0;

  return (
    <Page>
      <header className="flex items-center justify-between gap-3 pt-safe">
        <div className="pt-5">
          <p className="text-sm text-muted-foreground">
            {format.dateTime(now, { weekday: "long", day: "numeric", month: "long" })}
          </p>
          <h1 className="text-2xl font-bold tracking-tight">{t(greeting, { name: user.name })}</h1>
        </div>
        <Link to="/profile" className="pt-5" aria-label={t("level", { level: lvl.level })}>
          <LevelBadge level={lvl.level} />
        </Link>
      </header>

      {/* Level & streak */}
      <Card className="mt-5 gap-0 py-4">
        <CardContent className="px-4">
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold">
              {t("level", { level: lvl.level })} · {tr(rankForLevel(lvl.level))}
            </span>
            <span className="tabular-nums text-muted-foreground">{profile.xp} XP</span>
          </div>
          <XpBar progress={lvl.progress} className="mt-2" />
          <p className="mt-1.5 text-xs text-muted-foreground">
            {t("xpToNext", { xp: lvl.xpForNext - lvl.xpIntoLevel, next: lvl.level + 1 })}
          </p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 rounded-xl bg-muted/60 p-3">
              <Flame className={cn("size-6", d.stats.weekStreak > 0 ? "text-orange-500" : "text-muted-foreground")} />
              <div className="min-w-0">
                <p className="text-sm font-semibold leading-tight">{t("streak", { count: d.stats.weekStreak })}</p>
                {d.stats.weekStreak > 0 && <p className="text-xs text-muted-foreground">{t("streakHint")}</p>}
              </div>
            </div>
            <div className="rounded-xl bg-muted/60 p-3">
              <p className="text-xs text-muted-foreground">{t("weekGoal")}</p>
              <div className="mt-1.5 flex gap-1">
                {Array.from({ length: profile.weeklyGoal }).map((_, i) => (
                  <span
                    key={i}
                    className={cn(
                      "h-2 flex-1 rounded-full",
                      i < strengthThisWeek.length ? "bg-primary" : "bg-foreground/10",
                    )}
                  />
                ))}
              </div>
              <p className="mt-1.5 text-xs font-medium">
                {strengthThisWeek.length >= profile.weeklyGoal
                  ? t("weekGoalDone")
                  : t("weekGoalProgress", { done: strengthThisWeek.length, goal: profile.weeklyGoal })}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resume an open workout */}
      {d.open && (
        <Card className="mt-4 gap-0 border-primary/40 bg-primary/10 py-4">
          <CardContent className="flex items-center gap-3 px-4">
            <RotateCcw className="size-6 shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold">{lt(d.open.workoutName, locale)}</p>
              <p className="text-sm text-muted-foreground">{t("resumeHint")}</p>
            </div>
            <Button asChild>
              <Link to={`/train/${d.open.id}`}>{t("resumeWorkout")}</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Week strip */}
      {d.plan && (
        <section className="mt-6">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t("thisWeek")}</h2>
          <ol className="grid grid-cols-7 gap-1.5">
            {Array.from({ length: 7 }).map((_, i) => {
              const day = new Date(weekStart);
              day.setDate(day.getDate() + i);
              const done = d.thisWeek.filter((s) => weekdayIndex(s.startedAt) === i);
              const planned = d.plan!.trainingDays.includes(i)
                ? "strength"
                : d.plan!.cardioDays.includes(i)
                  ? "cardio"
                  : null;
              return (
                <li
                  key={i}
                  className={cn(
                    "flex flex-col items-center gap-1 rounded-xl border py-2 text-xs",
                    i === today && "border-primary ring-2 ring-primary/30",
                    done.length > 0 && "bg-primary text-primary-foreground border-primary",
                  )}
                >
                  <span className="font-medium">{tw(String(i) as "0")}</span>
                  <span className="text-[15px] font-bold tabular-nums">{day.getDate()}</span>
                  <span className="h-4">
                    {done.length > 0 ? (
                      "✓"
                    ) : planned === "strength" ? (
                      <span className="mx-auto block size-1.5 rounded-full bg-primary" />
                    ) : planned === "cardio" ? (
                      <Footprints className="mx-auto size-3.5 text-muted-foreground" />
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ol>
          <p className="mt-2 text-sm text-muted-foreground">
            {trainedToday
              ? t("todayDoneTitle")
              : isTrainingDay
                ? t("todayTraining")
                : isCardioDay
                  ? t("todayCardio")
                  : t("todayRest")}
          </p>
        </section>
      )}

      {/* Next workout */}
      {!d.plan ? (
        <Card className="mt-6 py-5">
          <CardContent className="px-5">
            <h2 className="text-lg font-semibold">{t("noPlanTitle")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("noPlanText")}</p>
            <Button asChild className="mt-4">
              <Link to="/plans">{t("choosePlan")}</Link>
            </Button>
          </CardContent>
        </Card>
      ) : d.next ? (
        <section className="mt-6">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t("nextWorkout")}
          </h2>
          <Card className="gap-0 overflow-hidden py-0">
            <div className="bg-gradient-to-br from-primary/20 via-primary/5 to-transparent px-5 pt-5 pb-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="text-2xl font-black tracking-tight">{lt(d.next.name, locale)}</h3>
                  <p className="text-sm text-muted-foreground">{lt(d.next.focus, locale)}</p>
                </div>
                <Badge variant="secondary">{tc("week", { week: d.week })}</Badge>
              </div>
              {d.phase && (
                <div className="mt-3 rounded-xl bg-background/70 p-3 text-sm">
                  <p>{lt(d.phase.goal, locale)}</p>
                  <p className="mt-1 flex items-center gap-1 font-medium">
                    <Sparkles className="size-4 text-xp" />
                    {t("rirTarget", { rir: d.phase.rir })}
                  </p>
                  {profile.beginnerMode && (
                    <p className="mt-1 flex gap-1 text-xs text-muted-foreground">
                      <Info className="mt-0.5 size-3.5 shrink-0" />
                      {t("rirExplain")}
                    </p>
                  )}
                </div>
              )}
            </div>
            <ol className="divide-y px-5">
              {d.next.exercises.map((we, i) => (
                <li key={we.id} className="flex items-center gap-3 py-2.5">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{lt(we.exercise.name, locale)}</span>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                    {effectiveSets(we.sets, i, d.phase)} × {we.targetMin}–{we.targetMax}
                    {we.exercise.type === "time" ? " s" : ""}
                  </span>
                </li>
              ))}
              {d.next.exercises.length === 0 && (
                <li className="py-4 text-sm text-muted-foreground">{t("noWorkouts")}</li>
              )}
            </ol>
            <div className="p-4">
              <PendingButton
                size="lg"
                className="h-14 w-full rounded-2xl text-base"
                disabled={d.next.exercises.length === 0}
                pending={pending === d.next.id}
                onClick={() => start(d.next!.id)}
              >
                <Play className="fill-current" /> {t("startWorkout")}
              </PendingButton>
              <p className="mt-2 text-center text-xs text-muted-foreground">
                {tc("exercises", { count: d.next.exercises.length })}
                {nextMinutes > 0 && <> · {t("duration", { min: nextMinutes })}</>}
              </p>
            </div>
          </Card>

          {d.plan.workouts.filter((w) => w.kind === "strength" && w.id !== d.next!.id).length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">{t("anyWorkout")}:</span>
              {d.plan.workouts
                .filter((w) => w.kind === "strength" && w.id !== d.next!.id)
                .map((w) => (
                  <PendingButton key={w.id} variant="outline" size="sm" pending={pending === w.id} onClick={() => start(w.id)}>
                    {lt(w.name, locale)}
                  </PendingButton>
                ))}
            </div>
          )}
        </section>
      ) : null}

      {/* Cardio */}
      {d.cardio.length > 0 && (
        <section className="mt-6">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t("cardioTitle")}
          </h2>
          <div className="space-y-2">
            {d.cardio.map((w) => (
              <Card key={w.id} className="gap-0 py-4">
                <CardContent className="flex items-center gap-3 px-4">
                  <div className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-chart-3/15 text-chart-3">
                    <Footprints className="size-6" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold">{lt(w.name, locale)}</p>
                    <p className="line-clamp-2 text-xs text-muted-foreground">{lt(w.focus, locale)}</p>
                    {w.exercises.length > 0 && (
                      <p className="mt-0.5 text-xs font-medium text-muted-foreground">
                        {t("duration", { min: workoutMinutes(w.exercises, null, false) })}
                      </p>
                    )}
                  </div>
                  <PendingButton
                    variant="secondary"
                    disabled={w.exercises.length === 0}
                    pending={pending === w.id}
                    onClick={() => start(w.id)}
                  >
                    {t("startCardio")}
                  </PendingButton>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Warm-up & tips */}
      {d.plan && (ll(d.plan.warmup, locale).length > 0 || ll(d.plan.tips, locale).length > 0) && (
        <Accordion type="multiple" className="mt-6 rounded-2xl border bg-card px-4">
          {ll(d.plan.warmup, locale).length > 0 && (
            <AccordionItem value="warmup">
              <AccordionTrigger className="text-base">{t("warmupTitle")}</AccordionTrigger>
              <AccordionContent>
                <ol className="list-decimal space-y-1.5 pl-5 text-sm">
                  {ll(d.plan.warmup, locale).map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ol>
              </AccordionContent>
            </AccordionItem>
          )}
          {ll(d.plan.tips, locale).length > 0 && (
            <AccordionItem value="tips">
              <AccordionTrigger className="text-base">{t("tipsTitle")}</AccordionTrigger>
              <AccordionContent>
                <ul className="list-disc space-y-1.5 pl-5 text-sm">
                  {ll(d.plan.tips, locale).map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      )}

      {/* Achievements */}
      {d.achievements.length > 0 && (
        <section className="mt-6">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {t("achievementsTitle")}
            </h2>
            <Link to="/profile#achievements" className="flex items-center text-sm font-medium text-primary">
              {t("viewAll")} <ChevronRight className="size-4" />
            </Link>
          </div>
          <div className="-mx-4 flex gap-2 no-scrollbar overflow-x-auto px-4 pb-1">
            {d.achievements.slice(0, 8).map((a) => (
              <div key={a.key} className="flex w-28 shrink-0 flex-col items-center rounded-2xl border bg-card p-3 text-center">
                <span className="text-3xl">{achievementIcon(a.key)}</span>
                <span className="mt-1 text-xs font-semibold leading-tight">
                  {ta(`${a.key}.title` as "first_workout.title")}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </Page>
  );
}
